import React, { useState, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { FacilityWithCondition } from '@/types/harbor-management.types';
import {
  FacilityCondition,
  facilityConditionOptions,
} from '@/constant';
import { FacilityConditionIcon } from '@/components/FacilityConditionIcon';
import { Settings } from 'lucide-react';
import clsx from 'clsx';
import { formatDateWithOptions, formatDisplayDate } from '@/utils/Date';
import {
  formatManagedListKey,
  useManagedListOptions,
} from '@/hooks/useManagedListOptions';
import { ManagedListType } from '@/types/list-value.types';

interface FacilityConditionManagerProps {
  facilities: string[];
  facilitiesWithConditions: FacilityWithCondition[];
  onFacilitiesChange: (facilities: string[]) => void;
  onFacilitiesWithConditionsChange: (
    facilitiesWithConditions: FacilityWithCondition[]
  ) => void;
  disabled?: boolean;
  errors?: string;
}

const FacilityConditionManager: React.FC<FacilityConditionManagerProps> = ({
  facilities,
  facilitiesWithConditions,
  onFacilitiesChange,
  onFacilitiesWithConditionsChange,
  disabled = false,
  errors,
}) => {
  const intl = useIntl();
  const [showConditionManager, setShowConditionManager] = useState(false);
  const { options: dynamicFacilityOptions } = useManagedListOptions(
    ManagedListType.HARBOR_FACILITY
  );

  const currentUser = useSelector((state: RootState) => state.auth.userData);
  const availableFacilities = useMemo(() => {
    const optionMap = new Map(
      dynamicFacilityOptions.map((option) => [
        option.value.toLowerCase(),
        {
          ...option,
          value: option.value.toLowerCase(),
        },
      ])
    );

    [...facilities, ...facilitiesWithConditions.map((item) => item.facility)].forEach(
      (value) => {
        const normalizedValue = String(value || '').toLowerCase();
        if (!normalizedValue || optionMap.has(normalizedValue)) return;

        optionMap.set(normalizedValue, {
          id: normalizedValue,
          value: normalizedValue,
          label: formatManagedListKey(normalizedValue),
        });
      }
    );

    return Array.from(optionMap.values()).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [dynamicFacilityOptions, facilities, facilitiesWithConditions]);

  const handleFacilityToggle = useCallback(
    (facilityValue: string, checked: boolean) => {
      const normalizedValue = facilityValue.toLowerCase();

      if (checked) {
        const updatedFacilities = [...facilities, normalizedValue];
        onFacilitiesChange(updatedFacilities);

        const newFacilityWithCondition: FacilityWithCondition = {
          facility: normalizedValue,
          condition: FacilityCondition.GOOD,
          last_updated: new Date().toISOString(),
          updated_by:
            currentUser && currentUser.id && currentUser.name
              ? {
                  id: currentUser.id,
                  name: currentUser.name,
                }
              : undefined,
          availability_status: 'always',
        };

        const updatedConditions = [
          ...facilitiesWithConditions,
          newFacilityWithCondition,
        ];
        onFacilitiesWithConditionsChange(updatedConditions);
      } else {
        const updatedFacilities = facilities.filter(
          (f) => f.toLowerCase() !== normalizedValue
        );
        onFacilitiesChange(updatedFacilities);

        const updatedConditions = facilitiesWithConditions.filter(
          (f) => f.facility.toLowerCase() !== normalizedValue
        );
        onFacilitiesWithConditionsChange(updatedConditions);
      }
    },
    [
      facilities,
      facilitiesWithConditions,
      onFacilitiesChange,
      onFacilitiesWithConditionsChange,
      currentUser,
    ]
  );

  const handleConditionChange = useCallback(
    (facilityValue: string, newCondition: string) => {
      const normalizedValue = facilityValue.toLowerCase();

      const existingFacilityIndex = facilitiesWithConditions.findIndex(
        (f) => f.facility.toLowerCase() === normalizedValue
      );

      let updatedConditions;

      if (existingFacilityIndex >= 0) {
        updatedConditions = facilitiesWithConditions.map((f) =>
          f.facility.toLowerCase() === normalizedValue
            ? {
                ...f,
                condition: newCondition,
                last_updated: new Date().toISOString(),
                updated_by:
                  currentUser && currentUser.id && currentUser.name
                    ? {
                        id: currentUser.id,
                        name: currentUser.name,
                      }
                    : f.updated_by,
              }
            : f
        );
      } else {
        const newFacilityWithCondition: FacilityWithCondition = {
          facility: normalizedValue,
          condition: newCondition,
          last_updated: new Date().toISOString(),
          updated_by:
            currentUser && currentUser.id && currentUser.name
              ? {
                  id: currentUser.id,
                  name: currentUser.name,
                }
              : undefined,
          availability_status: 'always',
        };
        updatedConditions = [
          ...facilitiesWithConditions,
          newFacilityWithCondition,
        ];
      }

      onFacilitiesWithConditionsChange(updatedConditions);
    },
    [facilitiesWithConditions, onFacilitiesWithConditionsChange, currentUser]
  );

  const getFacilityCondition = useCallback(
    (facilityValue: string) => {
      const normalizedValue = facilityValue.toLowerCase();
      const facilityWithCondition = facilitiesWithConditions.find(
        (f) => f.facility.toLowerCase() === normalizedValue
      );
      return facilityWithCondition?.condition || FacilityCondition.GOOD;
    },
    [facilitiesWithConditions]
  );

  const isFacilitySelected = useCallback(
    (facilityValue: string) => {
      const normalizedValue = facilityValue.toLowerCase();
      return facilities.some((f) => f.toLowerCase() === normalizedValue);
    },
    [facilities]
  );

  const getFacilityAvailability = useCallback(
    (facilityValue: string) => {
      const normalizedValue = facilityValue.toLowerCase();
      const facilityWithCondition = facilitiesWithConditions.find(
        (f) => f.facility.toLowerCase() === normalizedValue
      );
      return (
        facilityWithCondition?.availability_status ||
        (isFacilitySelected(facilityValue) ? 'always' : undefined)
      );
    },
    [facilitiesWithConditions, isFacilitySelected]
  );

  // Mirror HarborForm helpers for month/day UI
  const CURRENT_YEAR = new Date().getFullYear().toString();
  const getAvailableDays = (month: string) => {
    const monthNum = parseInt(month);
    return Array.from(
      { length: new Date(2000, monthNum, 0).getDate() },
      (_, i) => i + 1
    );
  };
  const getTranslatedMonthName = (monthIndex: number) => {
    const monthKeys = [
      'COMMON.MONTHS.JAN',
      'COMMON.MONTHS.FEB',
      'COMMON.MONTHS.MAR',
      'COMMON.MONTHS.APR',
      'COMMON.MONTHS.MAY',
      'COMMON.MONTHS.JUN',
      'COMMON.MONTHS.JUL',
      'COMMON.MONTHS.AUG',
      'COMMON.MONTHS.SEP',
      'COMMON.MONTHS.OCT',
      'COMMON.MONTHS.NOV',
      'COMMON.MONTHS.DEC',
    ];
    return intl
      ? intl.formatMessage({ id: monthKeys[monthIndex - 1] })
      : formatDateWithOptions(new Date(0, monthIndex - 1), { month: 'short' }, 'en');
  };

  const handleAvailabilityChange = useCallback(
    (facilityValue: string, status: 'always' | 'seasonal' | 'closed') => {
      const normalizedValue = facilityValue.toLowerCase();
      const updated = facilitiesWithConditions.map((f) =>
        f.facility.toLowerCase() === normalizedValue
          ? {
              ...f,
              availability_status: status,
              seasonal_availability:
                status === 'seasonal'
                  ? f.seasonal_availability || {
                      spring: [
                        { start_date: '', end_date: '', is_closed: false },
                      ],
                      summer: [
                        { start_date: '', end_date: '', is_closed: false },
                      ],
                      autumn: [
                        { start_date: '', end_date: '', is_closed: false },
                      ],
                      winter: [
                        { start_date: '', end_date: '', is_closed: false },
                      ],
                    }
                  : undefined,
              last_updated: new Date().toISOString(),
              updated_by:
                currentUser && currentUser.id && currentUser.name
                  ? { id: currentUser.id, name: currentUser.name }
                  : f.updated_by,
            }
          : f
      );
      onFacilitiesWithConditionsChange(updated);
    },
    [facilitiesWithConditions, onFacilitiesWithConditionsChange, currentUser]
  );

  // Seasonal availability helpers per facility
  type SeasonKey = 'spring' | 'summer' | 'autumn' | 'winter';
  type SeasonField = 'start_date' | 'end_date' | 'is_closed';

  const getFacilitySeasonRanges = useCallback(
    (facilityValue: string, season: SeasonKey) => {
      const normalizedValue = facilityValue.toLowerCase();
      const f = facilitiesWithConditions.find(
        (x) => x.facility.toLowerCase() === normalizedValue
      );
      return f?.seasonal_availability?.[season] || [];
    },
    [facilitiesWithConditions]
  );

  const addFacilitySeasonRange = useCallback(
    (facilityValue: string, season: SeasonKey, isClosed: boolean) => {
      const normalizedValue = facilityValue.toLowerCase();
      const updated = facilitiesWithConditions.map((f) => {
        if (f.facility.toLowerCase() !== normalizedValue) return f;
        const seasonal = f.seasonal_availability || {
          spring: [],
          summer: [],
          autumn: [],
          winter: [],
        };
        const ranges = seasonal[season] || [];
        return {
          ...f,
          seasonal_availability: {
            ...seasonal,
            [season]: [
              ...ranges,
              { start_date: '', end_date: '', is_closed: isClosed },
            ],
          },
          last_updated: new Date().toISOString(),
          updated_by:
            currentUser && currentUser.id && currentUser.name
              ? { id: currentUser.id, name: currentUser.name }
              : f.updated_by,
        };
      });
      onFacilitiesWithConditionsChange(updated);
    },
    [facilitiesWithConditions, onFacilitiesWithConditionsChange, currentUser]
  );

  const updateFacilitySeasonRange = useCallback(
    (
      facilityValue: string,
      season: SeasonKey,
      index: number,
      field: SeasonField,
      value: string | boolean
    ) => {
      const normalizedValue = facilityValue.toLowerCase();
      const updated = facilitiesWithConditions.map((f) => {
        if (f.facility.toLowerCase() !== normalizedValue) return f;
        const seasonal = f.seasonal_availability || {
          spring: [],
          summer: [],
          autumn: [],
          winter: [],
        };
        const ranges = [...(seasonal[season] || [])];
        if (!ranges[index]) {
          ranges[index] = { start_date: '', end_date: '', is_closed: false };
        }
        type SeasonRange = {
          start_date: string;
          end_date: string;
          is_closed: boolean;
        };
        const updatedRange: SeasonRange = { ...(ranges[index] as SeasonRange) };
        if (field === 'is_closed') {
          updatedRange.is_closed = Boolean(value);
        } else if (field === 'start_date') {
          updatedRange.start_date = String(value);
        } else {
          updatedRange.end_date = String(value);
        }
        ranges[index] = updatedRange;
        return {
          ...f,
          seasonal_availability: { ...seasonal, [season]: ranges },
          last_updated: new Date().toISOString(),
          updated_by:
            currentUser && currentUser.id && currentUser.name
              ? { id: currentUser.id, name: currentUser.name }
              : f.updated_by,
        };
      });
      onFacilitiesWithConditionsChange(updated);
    },
    [facilitiesWithConditions, onFacilitiesWithConditionsChange, currentUser]
  );

  const removeFacilitySeasonRange = useCallback(
    (facilityValue: string, season: SeasonKey, index: number) => {
      const normalizedValue = facilityValue.toLowerCase();
      const updated = facilitiesWithConditions.map((f) => {
        if (f.facility.toLowerCase() !== normalizedValue) return f;
        const seasonal = f.seasonal_availability || {
          spring: [],
          summer: [],
          autumn: [],
          winter: [],
        };
        const ranges = (seasonal[season] || []).filter((_, i) => i !== index);
        return {
          ...f,
          seasonal_availability: { ...seasonal, [season]: ranges },
          last_updated: new Date().toISOString(),
          updated_by:
            currentUser && currentUser.id && currentUser.name
              ? { id: currentUser.id, name: currentUser.name }
              : f.updated_by,
        };
      });
      onFacilitiesWithConditionsChange(updated);
    },
    [facilitiesWithConditions, onFacilitiesWithConditionsChange, currentUser]
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="block text-sm font-medium text-gray-700">
          {intl.formatMessage({ id: 'COMMON.FACILITIES' })}
        </span>
        <button
          type="button"
          onClick={() => setShowConditionManager(!showConditionManager)}
          className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-700 dark:text-blue-200 contrast:text-blue-200 bg-blue-50 dark:bg-blue-900 contrast:bg-blue-900 border border-blue-200 dark:border-blue-800 contrast:border-blue-800 rounded-md hover:bg-blue-100 dark:hover:bg-blue-800 contrast:hover:bg-blue-800 transition-colors"
          disabled={disabled}
        >
          <Settings className="w-3 h-3 mr-1" />
          {showConditionManager
            ? intl.formatMessage({
                id: 'HARBOR.FACILITY.CONDITION.HIDE_CONDITIONS',
              })
            : intl.formatMessage({
                id: 'HARBOR.FACILITY.CONDITION.MANAGE_CONDITIONS',
              })}
        </button>
      </div>

      {!showConditionManager ? (
        <div className="grid grid-cols-2 gap-4">
          {availableFacilities.map((facility) => {
            const isSelected = isFacilitySelected(facility.value);
            const condition = getFacilityCondition(facility.value);
            const checkboxId = `facility-${facility.value}`;

            return (
              <label
                htmlFor={checkboxId}
                key={facility.value}
                className="flex items-center gap-2"
              >
                <input
                  id={checkboxId}
                  type="checkbox"
                  name="facilities"
                  value={facility.value}
                  checked={isSelected}
                  onChange={(e) =>
                    handleFacilityToggle(facility.value, e.target.checked)
                  }
                  disabled={disabled}
                  className={clsx(
                    'h-5 w-5 text-blue-500 border-gray-200 rounded focus:ring-blue-500 cursor-pointer',
                    errors && 'border-red-500',
                    disabled ? 'bg-gray-100 cursor-not-allowed' : ''
                  )}
                />
                <span className="text-gray-700 flex-1 cursor-pointer">
                  {facility.label}
                </span>
                {isSelected && (
                  <FacilityConditionIcon condition={condition} size="sm" />
                )}
              </label>
            );
          })}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-blue-50 dark:bg-blue-950 contrast:bg-blue-950 border border-blue-200 dark:border-blue-800 contrast:border-blue-800 rounded-lg p-4">
            <h2 className="text-sm font-medium text-blue-900 dark:text-blue-200 contrast:text-blue-200 mb-2">
              {intl.formatMessage({
                id: 'HARBOR.FACILITY.CONDITION.MANAGEMENT_TITLE',
              })}
            </h2>
            <p className="text-xs text-blue-700 dark:text-blue-400 contrast:text-blue-200">
              {intl.formatMessage({
                id: 'HARBOR.FACILITY.CONDITION.MANAGEMENT_DESCRIPTION',
              })}
            </p>
          </div>

          <div className="space-y-3">
            {availableFacilities.map((facility) => {
              const isSelected = isFacilitySelected(facility.value);
              const condition = getFacilityCondition(facility.value);

              return (
                <div
                  key={facility.value}
                  className="border border-gray-200 rounded-lg p-4"
                >
                  <div className="flex items-center justify-between mb-3">
                    <label className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        name="facilities"
                        value={facility.value}
                        checked={isSelected}
                        onChange={(e) =>
                          handleFacilityToggle(facility.value, e.target.checked)
                        }
                        disabled={disabled}
                        className={clsx(
                          'h-5 w-5 text-blue-500 border-gray-200 rounded focus:ring-blue-500 cursor-pointer',
                          errors && 'border-red-500',
                          disabled ? 'bg-gray-100 cursor-not-allowed' : ''
                        )}
                      />
                      <span className="text-sm font-medium text-gray-900 cursor-pointer">
                        {facility.label}
                      </span>
                    </label>

                    {isSelected && (
                      <FacilityConditionIcon
                        condition={condition}
                        showLabel={true}
                        size="sm"
                      />
                    )}
                  </div>

                  {isSelected && (
                    <div className="ml-8">
                      <span className="block text-xs font-medium text-gray-600 mb-2">
                        {intl.formatMessage({
                          id: 'HARBOR.FACILITY.CONDITION.STATUS',
                        })}
                      </span>
                      <div className="grid grid-cols-2 gap-2">
                        {facilityConditionOptions.map((conditionOption) => (
                          <label
                            key={conditionOption.value}
                            className="flex items-center gap-2"
                          >
                            <input
                              type="radio"
                              name={`condition-${facility.value}`}
                              value={conditionOption.value}
                              checked={condition === conditionOption.value}
                              onChange={() => {
                                handleConditionChange(
                                  facility.value,
                                  conditionOption.value
                                );
                              }}
                              disabled={disabled}
                              className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                            />
                            <span className="text-xs text-gray-700">
                              {intl.formatMessage({
                                id: conditionOption.label,
                              })}
                            </span>
                          </label>
                        ))}
                      </div>

                      {/* Availability controls */}
                      <div className="mt-4">
                        <span className="block text-xs font-medium text-gray-600 mb-2">
                          {intl.formatMessage({
                            id: 'HARBOR.FACILITY.AVAILABILITY',
                          })}
                        </span>
                        <div className="flex flex-wrap gap-4">
                          {[
                            {
                              value: 'always',
                              label: 'HARBOR.FACILITY.AVAILABILITY.ALWAYS',
                            },
                            {
                              value: 'seasonal',
                              label: 'HARBOR.FACILITY.AVAILABILITY.SEASONAL',
                            },
                            {
                              value: 'closed',
                              label: 'HARBOR.FACILITY.AVAILABILITY.CLOSED',
                            },
                          ].map((opt) => {
                            const availability = getFacilityAvailability(
                              facility.value
                            );
                            return (
                              <label
                                key={opt.value}
                                className="inline-flex items-center gap-2"
                              >
                                <input
                                  type="radio"
                                  name={`availability-${facility.value}`}
                                  value={opt.value}
                                  checked={availability === opt.value}
                                  onChange={() =>
                                    handleAvailabilityChange(
                                      facility.value,
                                      opt.value as
                                        | 'always'
                                        | 'seasonal'
                                        | 'closed'
                                    )
                                  }
                                  disabled={disabled}
                                  className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                />
                                <span className="text-xs text-gray-700">
                                  {intl.formatMessage({ id: opt.label })}
                                </span>
                              </label>
                            );
                          })}
                        </div>

                        {getFacilityAvailability(facility.value) ===
                          'seasonal' && (
                          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {(
                              [
                                {
                                  key: 'spring',
                                  label: 'HARBOR.SEASON.SPRING',
                                  backgroundColor:
                                    'bg-blue-100 dark:bg-blue-900 contrast:bg-blue-900',
                                },
                                {
                                  key: 'summer',
                                  label: 'HARBOR.SEASON.SUMMER',
                                  backgroundColor:
                                    'bg-green-100 dark:bg-green-900 contrast:bg-blue-900',
                                },
                                {
                                  key: 'autumn',
                                  label: 'HARBOR.SEASON.AUTUMN',
                                  backgroundColor:
                                    'bg-orange-100 dark:bg-orange-900 contrast:bg-orange-900',
                                },
                                {
                                  key: 'winter',
                                  label: 'HARBOR.SEASON.WINTER',
                                  backgroundColor: 'bg-gray-100',
                                },
                              ] as Array<{
                                key: SeasonKey;
                                label: string;
                                backgroundColor: string;
                              }>
                            ).map((season) => (
                              <div
                                key={season.key}
                                className={
                                  season.backgroundColor +
                                  ' ' +
                                  'border rounded-md p-3'
                                }
                              >
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-md font-medium text-gray-700 dark:text-gray-900 contrast:text-gray-900">
                                    {intl.formatMessage({ id: season.label })}
                                  </span>
                                  <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      className={clsx(
                                        'text-[11px] px-1 py-1 rounded border',
                                        disabled
                                          ? 'opacity-50 cursor-not-allowed'
                                          : 'border-blue-600 dark:border-blue-300 contrast:border-blue-300 text-blue-700 dark:text-blue-200 hover:bg-blue-50 dark:hover:bg-white/10 contrast:text-blue-200 contrast:hover:bg-white/10'
                                      )}
                                      onClick={() =>
                                        addFacilitySeasonRange(
                                          facility.value,
                                          season.key,
                                          false
                                        )
                                      }
                                      disabled={disabled}
                                    >
                                      {intl.formatMessage({
                                        id: 'HARBOR.DATE_RANGE.ADD',
                                      })}
                                    </button>
                                  </div>
                                </div>

                                <div className="space-y-3">
                                  {getFacilitySeasonRanges(
                                    facility.value,
                                    season.key
                                  ).map((range, idx, seasonRanges) => (
                                    <div key={idx}>
                                      <div className="flex items-center justify-between">
                                        <div>
                                          <label className="inline-flex items-center cursor-pointer">
                                            <input
                                              type="checkbox"
                                              checked={!!range.is_closed}
                                              onChange={(e) =>
                                                updateFacilitySeasonRange(
                                                  facility.value,
                                                  season.key,
                                                  idx,
                                                  'is_closed',
                                                  e.target.checked
                                                )
                                              }
                                              className="sr-only peer cursor-pointer"
                                              disabled={disabled}
                                            />
                                            <div className="relative w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-red-500"></div>
                                            <span className="ms-3 text-xs font-medium text-gray-700 contrast:text-white/80">
                                              {range.is_closed
                                                ? intl.formatMessage({
                                                    id: 'HARBOR.DATE_RANGE.ADD_CLOSED',
                                                  })
                                                : intl.formatMessage({
                                                    id: 'HARBOR.DATE_RANGE.ADD_OPEN',
                                                  })}
                                            </span>
                                          </label>
                                        </div>
                                        {seasonRanges.length > 1 && (
                                          <button
                                            type="button"
                                            onClick={() =>
                                              removeFacilitySeasonRange(
                                                facility.value,
                                                season.key,
                                                idx
                                              )
                                            }
                                            className="text-red-500 hover:text-red-700"
                                            disabled={disabled}
                                            aria-label={intl.formatMessage({
                                              id: 'COMMON.REMOVE',
                                            })}
                                          >
                                            <svg
                                              className="w-5 h-5"
                                              fill="none"
                                              stroke="currentColor"
                                              viewBox="0 0 24 24"
                                            >
                                              <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                              />
                                            </svg>
                                          </button>
                                        )}
                                      </div>

                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                                        <div>
                                          <span className="block text-xs font-medium text-gray-700 dark:text-gray-900 contrast:text-gray-900 mb-1">
                                            {intl.formatMessage({
                                              id: 'COMMON.START_DATE',
                                            })}
                                          </span>
                                          <div className="flex gap-2">
                                            <select
                                              aria-label={`${intl.formatMessage({ id: 'COMMON.START_DATE' })} ${intl.formatMessage({ id: 'COMMON.MONTH' })}`}
                                              value={
                                                range.start_date?.split(
                                                  '-'
                                                )[1] || ''
                                              }
                                              onChange={(e) => {
                                                const [year] = (
                                                  range.start_date || ''
                                                ).split('-');
                                                const newDate = `${year || CURRENT_YEAR}-${e.target.value}-${range.start_date?.split('-')[2] || '01'}`;
                                                updateFacilitySeasonRange(
                                                  facility.value,
                                                  season.key,
                                                  idx,
                                                  'start_date',
                                                  newDate
                                                );
                                              }}
                                              disabled={disabled}
                                              className="flex-1 h-8 px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-light-active"
                                            >
                                              <option value="">
                                                {intl.formatMessage({
                                                  id: 'COMMON.MONTH',
                                                })}
                                              </option>
                                              {Array.from(
                                                { length: 12 },
                                                (_, i) => i + 1
                                              ).map((monthIndex) => (
                                                <option
                                                  key={monthIndex}
                                                  value={String(
                                                    monthIndex
                                                  ).padStart(2, '0')}
                                                >
                                                  {getTranslatedMonthName(
                                                    monthIndex
                                                  )}
                                                </option>
                                              ))}
                                            </select>
                                            <select
                                              aria-label={`${intl.formatMessage({ id: 'COMMON.START_DATE' })} ${intl.formatMessage({ id: 'COMMON.DAY' })}`}
                                              value={
                                                range.start_date?.split(
                                                  '-'
                                                )[2] || ''
                                              }
                                              onChange={(e) => {
                                                const [year, month] = (
                                                  range.start_date || ''
                                                ).split('-');
                                                const newDate = `${year || CURRENT_YEAR}-${month || '01'}-${e.target.value}`;
                                                updateFacilitySeasonRange(
                                                  facility.value,
                                                  season.key,
                                                  idx,
                                                  'start_date',
                                                  newDate
                                                );
                                              }}
                                              disabled={disabled}
                                              className="flex-1 h-8 px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-light-active"
                                            >
                                              <option value="">
                                                {intl.formatMessage({
                                                  id: 'COMMON.DAY',
                                                })}
                                              </option>
                                              {getAvailableDays(
                                                range.start_date?.split(
                                                  '-'
                                                )[1] || ''
                                              ).map((day: number) => (
                                                <option
                                                  key={day}
                                                  value={String(day).padStart(
                                                    2,
                                                    '0'
                                                  )}
                                                >
                                                  {day}
                                                </option>
                                              ))}
                                            </select>
                                          </div>
                                        </div>
                                        <div>
                                          <span className="block text-xs font-medium text-gray-700 dark:text-gray-900 contrast:text-gray-900 mb-1">
                                            {intl.formatMessage({
                                              id: 'COMMON.END_DATE',
                                            })}
                                          </span>
                                          <div className="flex gap-2">
                                            <select
                                              aria-label={`${intl.formatMessage({ id: 'COMMON.END_DATE' })} ${intl.formatMessage({ id: 'COMMON.MONTH' })}`}
                                              value={
                                                range.end_date?.split('-')[1] ||
                                                ''
                                              }
                                              onChange={(e) => {
                                                const [year] = (
                                                  range.end_date || ''
                                                ).split('-');
                                                const newDate = `${year || CURRENT_YEAR}-${e.target.value}-${range.end_date?.split('-')[2] || '01'}`;
                                                updateFacilitySeasonRange(
                                                  facility.value,
                                                  season.key,
                                                  idx,
                                                  'end_date',
                                                  newDate
                                                );
                                              }}
                                              disabled={disabled}
                                              className="flex-1 h-8 px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-light-active"
                                            >
                                              <option value="">
                                                {intl.formatMessage({
                                                  id: 'COMMON.MONTH',
                                                })}
                                              </option>
                                              {Array.from(
                                                { length: 12 },
                                                (_, i) => i + 1
                                              ).map((monthIndex) => (
                                                <option
                                                  key={monthIndex}
                                                  value={String(
                                                    monthIndex
                                                  ).padStart(2, '0')}
                                                >
                                                  {getTranslatedMonthName(
                                                    monthIndex
                                                  )}
                                                </option>
                                              ))}
                                            </select>
                                            <select
                                              aria-label={`${intl.formatMessage({ id: 'COMMON.END_DATE' })} ${intl.formatMessage({ id: 'COMMON.DAY' })}`}
                                              value={
                                                range.end_date?.split('-')[2] ||
                                                ''
                                              }
                                              onChange={(e) => {
                                                const [year, month] = (
                                                  range.end_date || ''
                                                ).split('-');
                                                const newDate = `${year || CURRENT_YEAR}-${month || '01'}-${e.target.value}`;
                                                updateFacilitySeasonRange(
                                                  facility.value,
                                                  season.key,
                                                  idx,
                                                  'end_date',
                                                  newDate
                                                );
                                              }}
                                              disabled={disabled}
                                              className="flex-1 h-8 px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-light-active"
                                            >
                                              <option value="">
                                                {intl.formatMessage({
                                                  id: 'COMMON.DAY',
                                                })}
                                              </option>
                                              {getAvailableDays(
                                                range.end_date?.split('-')[1] ||
                                                  ''
                                              ).map((day: number) => (
                                                <option
                                                  key={day}
                                                  value={String(day).padStart(
                                                    2,
                                                    '0'
                                                  )}
                                                >
                                                  {day}
                                                </option>
                                              ))}
                                            </select>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Show last updated information */}
                      {(() => {
                        const facilityWithCondition =
                          facilitiesWithConditions.find(
                            (f) =>
                              f.facility.toLowerCase() ===
                              facility.value.toLowerCase()
                          );

                        if (facilityWithCondition?.last_updated) {
                          return (
                            <div className="mt-2 text-xs text-gray-500">
                              {intl.formatMessage({
                                id: 'HARBOR.FACILITY.CONDITION.LAST_UPDATED',
                              })}
                              :{' '}
                              {formatDisplayDate(new Date(
                                facilityWithCondition.last_updated
                              ))}{' '}
                              {new Date(
                                facilityWithCondition.last_updated
                              ).toLocaleTimeString()}
                              {facilityWithCondition.updated_by && (
                                <>
                                  {' '}
                                  {intl.formatMessage({
                                    id: 'HARBOR.FACILITY.CONDITION.BY',
                                  })}{' '}
                                  <span className="font-medium">
                                    {facilityWithCondition.updated_by.name}
                                  </span>
                                </>
                              )}
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {errors && (
        <p className="text-red-500 text-xs mt-1" role="alert">
          {errors}
        </p>
      )}

      {/* Summary of selected facilities with conditions */}
      {facilities.length > 0 && (
        <div className="mt-4 p-3 bg-gray-100 border border-gray-200 rounded-lg">
          <h2 className="text-xs font-medium text-gray-700 mb-2">
            {intl.formatMessage(
              { id: 'HARBOR.FACILITY.CONDITION.SELECTED_FACILITIES' },
              { count: facilities.length }
            )}
          </h2>
          <div className="flex flex-wrap gap-2">
            {facilities.map((facilityValue) => {
              const facility = availableFacilities.find(
                (f) => f.value.toLowerCase() === facilityValue.toLowerCase()
              );
              const condition = getFacilityCondition(facilityValue);

              return (
                <div
                  key={facilityValue}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-gray-200 border border-gray-200 rounded-md text-xs"
                >
                  <span>
                    {facility ? facility.label : formatManagedListKey(facilityValue)}
                  </span>
                  <FacilityConditionIcon condition={condition} size="sm" />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default FacilityConditionManager;
